import React, { useState, useEffect, useCallback } from 'react';
import { 
  FiDownload, 
  FiUsers, 
  FiFileText, 
  FiDollarSign, 
  FiX, 
  FiPlay, 
  FiRefreshCw, 
  FiPackage, 
  FiTrendingUp, 
  FiTrendingDown,
  FiCalendar,
  FiPieChart,
  FiBarChart2
} from 'react-icons/fi';
import { FaRupeeSign } from 'react-icons/fa';
import { reportAPI } from '../utils/api';
import '../styles/reports.css';
import { Link } from 'react-router-dom';

const FILTER_OPTIONS = {
  profit_loss: {
    status: [],
    limit: []
  },
  invoices: {
    status: [
      { value: '', label: 'All' },
      { value: 'paid', label: 'Paid' },
      { value: 'pending', label: 'Pending' },
      { value: 'overdue', label: 'Overdue' },
      { value: 'cancelled', label: 'Cancelled' },
    ],
    limit: [
      { value: '', label: 'All' },
      { value: 25, label: '25' },
      { value: 50, label: '50' },
      { value: 100, label: '100' },
      { value: 200, label: '200' },
    ],
  },
  customers: {
    status: [
      { value: '', label: 'All' },
      { value: 'Active', label: 'Active' },
      { value: 'Inactive', label: 'Inactive' },
      { value: 'Suspended', label: 'Suspended' },
    ],
    limit: [
      { value: '', label: 'All' },
      { value: 25, label: '25' },
      { value: 50, label: '50' },
      { value: 100, label: '100' },
      { value: 200, label: '200' },
    ],
  },
  inventory: {
    status: [
      { value: '', label: 'All' },
    ],
    limit: [
      { value: '', label: 'All' },
      { value: 25, label: '25' },
      { value: 50, label: '50' },
      { value: 100, label: '100' },
      { value: 200, label: '200' },
    ],
  },
  transactions: {
    status: [
      { value: '', label: 'All' },
      { value: 'completed', label: 'Completed' },
      { value: 'pending', label: 'Pending' },
      { value: 'cancelled', label: 'Cancelled' },
    ],
    limit: [
      { value: '', label: 'All' },
      { value: 25, label: '25' },
      { value: 50, label: '50' },
      { value: 100, label: '100' },
      { value: 200, label: '200' },
    ],
  },
};

const PERIOD_OPTIONS = [
  { id: 'last_month', label: 'Last Month' },
  { id: 'last_30_days', label: 'Last 30 Days' },
  { id: 'last_6_months', label: 'Last 6 Months' },
  { id: 'this_month', label: 'This Month' },
  { id: 'this_year', label: 'This Year' },
  { id: 'custom', label: 'Custom Range' },
];

const ReportsPage = () => {
  const [activeTab, setActiveTab] = useState('profit_loss');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    status: '',
    limit: ''
  });
  const [appliedFilters, setAppliedFilters] = useState({ ...filters });
  const [reports, setReports] = useState({
    invoices: [],
    customers: [],
    inventory: [],
    transactions: [],
    financial: null
  });

  // Dedicated Profit & Loss state
  const [plPeriod, setPlPeriod] = useState('last_6_months');
  const [plReport, setPlReport] = useState(null);
  const [plLoading, setPlLoading] = useState(false);

  // Fetch Profit & Loss report
  const fetchProfitLoss = useCallback(async (period = plPeriod, customDates = appliedFilters) => {
    setPlLoading(true);
    setError(null);
    try {
      const params = { period };
      if (period === 'custom') {
        if (customDates.startDate) params.startDate = customDates.startDate;
        if (customDates.endDate) params.endDate = customDates.endDate;
      }
      const data = await reportAPI.getProfitLossReport(params);
      setPlReport(data);
    } catch (err) {
      console.error('Error fetching P&L report:', err);
      setError(err.message || 'Failed to load Profit & Loss report');
    } finally {
      setPlLoading(false);
    }
  }, [plPeriod, appliedFilters]);

  // Fetch standard report data for other tabs
  const fetchReport = useCallback(async (type, filterSet = appliedFilters) => {
    if (type === 'profit_loss') return;
    setLoading(true);
    setError(null);
    try {
      let params = { ...filterSet };
      if (!params.limit) delete params.limit;
      let data;
      switch (type) {
        case 'invoices':
          data = await reportAPI.getInvoiceReport(params);
          break;
        case 'customers':
          data = await reportAPI.getCustomerReport(params);
          break;
        case 'inventory':
          data = await reportAPI.getInventoryReport(params);
          break;
        case 'transactions':
          data = await reportAPI.getTransactionReport(params);
          break;
        default:
          throw new Error('Invalid report type');
      }

      // Always fetch financial summary for general tabs
      let month = '';
      let year = '';
      if (params.startDate) {
        const date = new Date(params.startDate);
        month = date.getMonth() + 1;
        year = date.getFullYear();
      }
      const financialData = await reportAPI.getFinancialSummary({ month, year });

      setReports(prev => ({ ...prev, [type]: data, financial: financialData }));
    } catch (err) {
      setError(err.message);
      console.error('Error fetching report:', err);
    } finally {
      setLoading(false);
    }
  }, [appliedFilters]);

  // Load data when activeTab changes
  useEffect(() => {
    if (activeTab === 'profit_loss') {
      fetchProfitLoss(plPeriod, appliedFilters);
    } else {
      fetchReport(activeTab, appliedFilters);
    }
  }, [activeTab, plPeriod, appliedFilters, fetchProfitLoss, fetchReport]);

  // Handle filter changes (edit, apply, reset, remove)
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };
  const handleApplyFilters = () => {
    setAppliedFilters({ ...filters });
  };
  const handleResetFilters = () => {
    setFilters({ startDate: '', endDate: '', status: '', limit: '' });
    setAppliedFilters({ startDate: '', endDate: '', status: '', limit: '' });
  };
  const handleRemoveFilter = (key) => {
    setFilters(prev => ({ ...prev, [key]: '' }));
    setAppliedFilters(prev => ({ ...prev, [key]: '' }));
  };

  // CSV download helper
  const downloadBlob = (csvContent, fileName) => {
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  // CSV conversion
  const convertToCSV = (data) => {
    if (!data || data.length === 0) return '';
    const headers = Object.keys(data[0]);
    const csvRows = [headers.join(',')];
    data.forEach(row => {
      const values = headers.map(header => {
        const value = row[header];
        return typeof value === 'string' ? `"${value.replace(/"/g, '""')}"` : value;
      });
      csvRows.push(values.join(','));
    });
    return csvRows.join('\n');
  };

  // Download standard report
  const handleDownload = (type) => {
    const data = reports[type];
    if (!data || data.length === 0) {
      alert('No data to download');
      return;
    }
    const csvContent = convertToCSV(data);
    downloadBlob(csvContent, `${type}_report_${new Date().toISOString().split('T')[0]}.csv`);
  };

  // Download Profit & Loss report (Monthly breakdown or Invoices)
  const handleDownloadPL = (mode = 'monthly') => {
    if (!plReport) return;
    if (mode === 'monthly' && plReport.monthlyBreakdown?.length > 0) {
      const data = plReport.monthlyBreakdown.map(m => ({
        'Month': m.monthLabel,
        'Invoices': m.invoicesCount,
        'Units Sold': m.itemsSold,
        'Sales Revenue (Rs)': m.revenue.toFixed(2),
        'Expenses Cost (Rs)': m.cost.toFixed(2),
        'GST Output (Rs)': m.gst.toFixed(2),
        'Net Profit (Rs)': m.profit.toFixed(2),
        'Margin (%)': `${m.margin}%`,
        'Status': m.isProfit ? 'PROFIT' : 'LOSS'
      }));
      const csv = convertToCSV(data);
      downloadBlob(csv, `monthly_profit_loss_${plPeriod}_${new Date().toISOString().split('T')[0]}.csv`);
    } else if (plReport.invoices?.length > 0) {
      const data = plReport.invoices.map(inv => ({
        'Invoice #': inv.invoice_number,
        'Date': inv.invoice_date,
        'Customer': inv.bill_to_name,
        'Total Sales (Rs)': inv.total.toFixed(2),
        'GST (Rs)': inv.gst.toFixed(2),
        'Expenses Cost (Rs)': inv.cost.toFixed(2),
        'Net Profit (Rs)': inv.netProfit.toFixed(2),
        'Margin (%)': `${inv.profitMargin}%`,
        'Status': inv.isProfit ? 'PROFIT' : 'LOSS'
      }));
      const csv = convertToCSV(data);
      downloadBlob(csv, `invoices_profit_loss_${plPeriod}_${new Date().toISOString().split('T')[0]}.csv`);
    }
  };

  // Stats for standard tabs
  const getReportStats = (type) => {
    const data = reports[type];
    if (!data || data.length === 0) return { count: 0, total: 0 };
    switch (type) {
      case 'invoices':
        return {
          count: data.length,
          total: data.reduce((sum, item) => sum + parseFloat(item.total || 0), 0)
        };
      case 'customers':
        return {
          count: data.length,
          total: data.reduce((sum, item) => sum + parseFloat(item.total_spent || 0), 0)
        };
      case 'inventory':
        return {
          count: data.length,
          total: data.reduce((sum, item) => sum + parseFloat(item.stock_value || 0), 0)
        };
      case 'transactions':
        return {
          count: data.length,
          total: data.reduce((sum, item) => sum + parseFloat(item.amount || 0), 0)
        };
      default:
        return { count: 0, total: 0 };
    }
  };

  // Financial summary header for standard tabs
  const renderFinancialSummary = () => {
    if (activeTab === 'profit_loss') return null;
    const data = reports.financial;
    if (!data) return null;
    return (
      <div className="financial-summary-dashboard" style={{ marginTop: '20px', marginBottom: '30px' }}>
        <div className="summary-cards" style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
          <div className="summary-card" style={{ flex: '1', minWidth: '220px', background: 'rgba(255,255,255,0.08)', padding: '20px', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.15)' }}>
            <h3 style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem', marginBottom: '8px' }}>Total Sales (Invoices)</h3>
            <h2 style={{ color: '#10b981', margin: '0 0 6px 0', fontSize: '1.6rem' }}>₹{data.sales?.total?.toFixed(2) || '0.00'}</h2>
            <small style={{ color: 'rgba(255,255,255,0.6)' }}>{data.sales?.invoicesCount || 0} Invoices</small>
          </div>
          <div className="summary-card" style={{ flex: '1', minWidth: '220px', background: 'rgba(255,255,255,0.08)', padding: '20px', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.15)' }}>
            <h3 style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem', marginBottom: '8px' }}>Cost of Goods (Expenses)</h3>
            <h2 style={{ color: '#ef4444', margin: '0 0 6px 0', fontSize: '1.6rem' }}>₹{data.cost?.total?.toFixed(2) || '0.00'}</h2>
            <small style={{ color: 'rgba(255,255,255,0.6)' }}>{data.sales?.itemsSold || 0} Items Sold</small>
          </div>
          <div className="summary-card" style={{ flex: '1', minWidth: '220px', background: 'rgba(255,255,255,0.08)', padding: '20px', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.15)' }}>
            <h3 style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem', marginBottom: '8px' }}>GST Collected</h3>
            <h2 style={{ color: '#38bdf8', margin: '0 0 6px 0', fontSize: '1.6rem' }}>₹{data.sales?.outputGst?.toFixed(2) || '0.00'}</h2>
            <small style={{ color: 'rgba(255,255,255,0.6)' }}>Output Tax from Invoices</small>
          </div>
          <div className="summary-card" style={{ flex: '1', minWidth: '220px', background: 'rgba(255,255,255,0.08)', padding: '20px', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.15)' }}>
            <h3 style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem', marginBottom: '8px' }}>Net Profit</h3>
            <h2 style={{ color: (data.summary?.netProfit || 0) >= 0 ? '#10b981' : '#ef4444', margin: '0 0 6px 0', fontSize: '1.6rem' }}>
              ₹{data.summary?.netProfit?.toFixed(2) || '0.00'}
            </h2>
            <small style={{ color: 'rgba(255,255,255,0.6)' }}>
              Profit Margin: {data.summary?.profitMargin ? `${data.summary.profitMargin.toFixed(1)}%` : '0.0%'}
            </small>
          </div>
        </div>
      </div>
    );
  };

  // Render comprehensive Profit & Loss Business Dashboard
  const renderProfitLossDashboard = () => {
    const summary = plReport?.summary || {};
    const monthlyBreakdown = plReport?.monthlyBreakdown || [];
    const invoices = plReport?.invoices || [];
    const isProfit = summary.isProfit !== undefined ? summary.isProfit : (summary.netProfit >= 0);

    return (
      <div className="pl-dashboard-container">
        {/* 1. Period Selector Card */}
        <div className="pl-period-selector-card">
          <div className="pl-period-header">
            <div className="pl-period-title">
              <FiCalendar /> Business Performance Period
            </div>
            {plReport?.startDate && plReport?.endDate && (
              <div className="pl-period-date-range">
                {plReport.startDate} — {plReport.endDate}
              </div>
            )}
          </div>

          <div className="pl-period-buttons">
            {PERIOD_OPTIONS.map(opt => (
              <button
                key={opt.id}
                className={`pl-period-btn ${plPeriod === opt.id ? 'active' : ''}`}
                onClick={() => {
                  setPlPeriod(opt.id);
                  if (opt.id !== 'custom') {
                    fetchProfitLoss(opt.id);
                  }
                }}
              >
                {opt.label}
              </button>
            ))}
          </div>

          {plPeriod === 'custom' && (
            <div className="pl-custom-inputs">
              <label>
                <span>From:</span>
                <input
                  type="date"
                  value={filters.startDate}
                  onChange={e => handleFilterChange('startDate', e.target.value)}
                />
              </label>
              <label>
                <span>To:</span>
                <input
                  type="date"
                  value={filters.endDate}
                  onChange={e => handleFilterChange('endDate', e.target.value)}
                />
              </label>
              <button 
                className="pill-btn run-btn" 
                onClick={() => {
                  handleApplyFilters();
                  fetchProfitLoss('custom', filters);
                }}
              >
                <FiPlay /> Apply Range
              </button>
            </div>
          )}
        </div>

        {/* Loading Indicator */}
        {plLoading && (
          <div className="loading-state">
            <p>Loading Profit & Loss data...</p>
          </div>
        )}

        {/* 2. Executive KPI Summary Cards */}
        {!plLoading && (
          <div className="pl-kpi-grid">
            {/* Total Sales Revenue */}
            <div className="pl-kpi-card card-sales">
              <div className="pl-kpi-icon icon-sales">
                <FiDollarSign />
              </div>
              <div className="pl-kpi-body">
                <div className="pl-kpi-label">Sales Revenue</div>
                <div className="pl-kpi-value">
                  ₹{(summary.totalSales || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
                <div className="pl-kpi-subtext">
                  <span><strong>{summary.invoicesCount || 0}</strong> Invoices • <strong>{summary.itemsSold || 0}</strong> Units Sold</span>
                </div>
              </div>
            </div>

            {/* Total Expenses (Cost of Goods Sold) */}
            <div className="pl-kpi-card card-expenses">
              <div className="pl-kpi-icon icon-expenses">
                <FiTrendingDown />
              </div>
              <div className="pl-kpi-body">
                <div className="pl-kpi-label">Inventory &amp; Expenses</div>
                <div className="pl-kpi-value">
                  ₹{(summary.totalExpenses || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
                <div className="pl-kpi-subtext">
                  <span>Cost of Goods Sold (COGS)</span>
                </div>
              </div>
            </div>

            {/* Net Profit & Loss */}
            <div className={`pl-kpi-card card-profit ${isProfit ? 'is-profit' : 'is-loss'}`}>
              <div className={`pl-kpi-icon ${isProfit ? 'icon-profit' : 'icon-loss'}`}>
                {isProfit ? <FiTrendingUp /> : <FiTrendingDown />}
              </div>
              <div className="pl-kpi-body">
                <div className="pl-kpi-label-row">
                  <span className="pl-kpi-label">Net Profit &amp; Loss</span>
                  <span className={`pl-badge-pill ${isProfit ? 'pl-badge-profit' : 'pl-badge-loss'}`}>
                    {isProfit ? 'PROFIT' : 'LOSS'}
                  </span>
                </div>
                <div className={`pl-kpi-value ${isProfit ? 'pl-text-profit' : 'pl-text-loss'}`}>
                  {isProfit ? '+' : '-'}₹{Math.abs(summary.netProfit || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
                <div className="pl-kpi-subtext">
                  <span style={{ fontWeight: 600, color: isProfit ? '#15803d' : '#b91c1c' }}>
                    {summary.profitMargin || 0}% Profit Margin
                  </span>
                </div>
              </div>
            </div>

            {/* Total GST Output */}
            <div className="pl-kpi-card card-tax">
              <div className="pl-kpi-icon icon-tax">
                <FiPieChart />
              </div>
              <div className="pl-kpi-body">
                <div className="pl-kpi-label">GST Output Tax</div>
                <div className="pl-kpi-value">
                  ₹{(summary.totalGST || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
                <div className="pl-kpi-subtext">
                  <span>Tax collected from invoices</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 3. Month-by-Month Business Breakdown (Last 6 Months & Period Comparison) */}
        {!plLoading && monthlyBreakdown.length > 0 && (
          <div className="pl-section">
            <div className="pl-section-header">
              <div className="pl-section-title">
                <FiBarChart2 /> Month-by-Month Business Performance
              </div>
              <button 
                className="btn btn-secondary btn-sm"
                onClick={() => handleDownloadPL('monthly')}
                style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
              >
                <FiDownload /> Download Monthly CSV
              </button>
            </div>

            <div className="pl-table-container">
              <table className="pl-data-table">
                <thead>
                  <tr>
                    <th>Month</th>
                    <th style={{ textAlign: 'center' }}>Invoices</th>
                    <th style={{ textAlign: 'center' }}>Units Sold</th>
                    <th style={{ textAlign: 'right' }}>Sales Revenue</th>
                    <th style={{ textAlign: 'right' }}>Cost / Expenses</th>
                    <th style={{ textAlign: 'right' }}>GST Output</th>
                    <th style={{ textAlign: 'right' }}>Net Profit / Loss</th>
                    <th style={{ textAlign: 'center' }}>Margin</th>
                    <th style={{ textAlign: 'center' }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {monthlyBreakdown.map(month => (
                    <tr key={month.monthKey}>
                      <td className="pl-month-name">{month.monthLabel}</td>
                      <td style={{ textAlign: 'center' }}>{month.invoicesCount}</td>
                      <td style={{ textAlign: 'center' }}>{month.itemsSold}</td>
                      <td style={{ textAlign: 'right', fontWeight: 600 }}>
                        ₹{month.revenue.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                      </td>
                      <td style={{ textAlign: 'right', color: '#ea580c' }}>
                        ₹{month.cost.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                      </td>
                      <td style={{ textAlign: 'right', color: '#2563eb' }}>
                        ₹{month.gst.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                      </td>
                      <td style={{ textAlign: 'right', fontWeight: 700 }} className={month.isProfit ? 'pl-text-profit' : 'pl-text-loss'}>
                        {month.isProfit ? '+' : '-'}₹{Math.abs(month.profit).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                      </td>
                      <td style={{ textAlign: 'center', fontWeight: 600 }}>
                        {month.margin}%
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <span className={`pl-badge-pill ${month.isProfit ? 'pl-badge-profit' : 'pl-badge-loss'}`}>
                          {month.isProfit ? 'PROFIT' : 'LOSS'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* 4. Invoices P&L Breakdown Table */}
        {!plLoading && invoices.length > 0 && (
          <div className="pl-section">
            <div className="pl-section-header">
              <div className="pl-section-title">
                <FiFileText /> Invoices Profit &amp; Loss Details ({invoices.length})
              </div>
              <button 
                className="btn btn-primary btn-sm"
                onClick={() => handleDownloadPL('invoices')}
                style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
              >
                <FiDownload /> Download Invoices CSV
              </button>
            </div>

            <div className="pl-table-container">
              <table className="pl-data-table">
                <thead>
                  <tr>
                    <th>Invoice #</th>
                    <th>Date</th>
                    <th>Customer</th>
                    <th style={{ textAlign: 'right' }}>Total (Sales)</th>
                    <th style={{ textAlign: 'right' }}>GST</th>
                    <th style={{ textAlign: 'right' }}>Cost / Expense</th>
                    <th style={{ textAlign: 'right' }}>Net Profit</th>
                    <th style={{ textAlign: 'center' }}>Margin</th>
                    <th style={{ textAlign: 'center' }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map(inv => (
                    <tr key={inv.id || inv.invoice_number}>
                      <td style={{ fontWeight: 600 }}>{inv.invoice_number}</td>
                      <td>{inv.invoice_date ? new Date(inv.invoice_date).toLocaleDateString() : '-'}</td>
                      <td>{inv.bill_to_name || 'Walk-in Customer'}</td>
                      <td style={{ textAlign: 'right', fontWeight: 600 }}>
                        ₹{inv.total.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                      </td>
                      <td style={{ textAlign: 'right', color: '#64748b' }}>
                        ₹{inv.gst.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                      </td>
                      <td style={{ textAlign: 'right', color: '#ea580c' }}>
                        ₹{inv.cost.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                      </td>
                      <td style={{ textAlign: 'right', fontWeight: 700 }} className={inv.isProfit ? 'pl-text-profit' : 'pl-text-loss'}>
                        {inv.isProfit ? '+' : '-'}₹{Math.abs(inv.netProfit).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                      </td>
                      <td style={{ textAlign: 'center', fontWeight: 600 }}>
                        {inv.profitMargin}%
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <span className={`pl-badge-pill ${inv.isProfit ? 'pl-badge-profit' : 'pl-badge-loss'}`}>
                          {inv.isProfit ? 'PROFIT' : 'LOSS'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {!plLoading && invoices.length === 0 && (
          <div className="no-data" style={{ padding: '40px', background: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
            <p>No invoice sales or expenses recorded for this period ({plPeriod.replace(/_/g, ' ')}).</p>
          </div>
        )}
      </div>
    );
  };

  // Standard Table for invoices, customers, inventory, transactions
  const renderTable = (type) => {
    const data = reports[type];

    if (!data || data.length === 0) {
      return <p className="no-data">No data available</p>;
    }
    const headers = Object.keys(data[0]);
    return (
      <div className="table-container">
        <table className="report-table">
          <thead>
            <tr>
              {headers.map(header => (
                <th key={header}>{header.replace(/_/g, ' ').toUpperCase()}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((row, index) => (
              <tr key={index}>
                {headers.map(header => (
                  <td key={header}>
                    {header.includes('date') || header.includes('created_at') || header.includes('last_invoice')
                      ? (row[header] && row[header] !== '-' ? new Date(row[header]).toLocaleDateString() : '-')
                      : header.includes('amount') || header.includes('total') || header.includes('spent') || header.includes('price') || header.includes('profit') || header.includes('value')
                      ? `₹${parseFloat(row[header] || 0).toFixed(2)}`
                      : (row[header] !== null && row[header] !== undefined ? row[header] : '-')}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  // Tabs definition
  const tabs = [
    { id: 'profit_loss', label: 'Profit & Loss (P&L)', icon: FiTrendingUp },
    { id: 'invoices', label: 'Invoices', icon: FiFileText },
    { id: 'customers', label: 'Customers', icon: FiUsers },
    { id: 'inventory', label: 'Inventory', icon: FiPackage },
    { id: 'transactions', label: 'Transactions', icon: FaRupeeSign }
  ];

  // Breadcrumbs
  const breadcrumbs = [
    { label: 'Home', link: '/Dashboard' },
    { label: 'Reports', link: '/reports' },
    { label: tabs.find(t => t.id === activeTab)?.label, link: '#' }
  ];

  // Filter chips for standard tabs
  const filterChips = [
    filters.startDate && {
      key: 'startDate',
      label: `Start: ${filters.startDate}`
    },
    filters.endDate && {
      key: 'endDate',
      label: `End: ${filters.endDate}`
    },
    filters.status && {
      key: 'status',
      label: `Status: ${FILTER_OPTIONS[activeTab]?.status.find(o => o.value === filters.status)?.label}`
    },
    filters.limit && {
      key: 'limit',
      label: `Limit: ${filters.limit}`
    }
  ].filter(Boolean);

  return (
    <main className="page-container reports-main">
      {/* Breadcrumbs and Title */}
      <div className="reports-breadcrumbs">
        {breadcrumbs.map((b, i) => (
          <Link to={b.link} key={b.label} className="breadcrumb">
            {i > 0 && <span className="breadcrumb-sep">/</span>}
            {b.label} 
          </Link>
        ))}
      </div>
      <div className="page-header reports-header">
        <h1>{tabs.find(t => t.id === activeTab)?.label} Report</h1>
      </div>

      {/* Financial Summary for standard tabs */}
      {renderFinancialSummary()}

      {/* Tabs */}
      <div className="report-tabs pill-tabs">
        {tabs.map(tab => (
          <button
            key={tab.id}
            className={`tab-button pill ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            <tab.icon />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Profit & Loss dedicated view */}
      {activeTab === 'profit_loss' ? (
        renderProfitLossDashboard()
      ) : (
        <>
          {/* Filters Section for standard tabs */}
          <div className="filters-section pill-filters">
            <div className="pill-filter-row">
              <div className="pill-filter-chips">
                {filterChips.length === 0 && <span className="no-active-filters">No filters applied</span>}
                {filterChips.map(chip => (
                  <span className="filter-chip" key={chip.key}>
                    {chip.label}
                    <button className="chip-remove" onClick={() => handleRemoveFilter(chip.key)}><FiX /></button>
                  </span>
                ))}
              </div>
              <div className="pill-filter-controls">
                <label>
                  <span>Start Date</span>
                  <input
                    type="date"
                    value={filters.startDate}
                    onChange={e => handleFilterChange('startDate', e.target.value)}
                  />
                </label>
                <label>
                  <span>End Date</span>
                  <input
                    type="date"
                    value={filters.endDate}
                    onChange={e => handleFilterChange('endDate', e.target.value)}
                  />
                </label>
                <label>
                  <span>Status</span>
                  <select
                    value={filters.status}
                    onChange={e => handleFilterChange('status', e.target.value)}
                  >
                    {FILTER_OPTIONS[activeTab]?.status.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </label>
                <label>
                  <span>Limit</span>
                  <select
                    value={filters.limit}
                    onChange={e => handleFilterChange('limit', e.target.value)}
                  >
                    {FILTER_OPTIONS[activeTab]?.limit.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </label>
                <button className="pill-btn run-btn" onClick={handleApplyFilters}><FiPlay /> Run</button>
                <button className="pill-btn reset-btn" onClick={handleResetFilters}><FiRefreshCw /> Reset</button>
              </div>
            </div>
          </div>

          {/* Stats for standard tabs */}
          <div className="report-stats pill-stats">
            <div className="stat-card">
              <h3>Total Records</h3>
              <p>{getReportStats(activeTab).count}</p>
            </div>
            <div className="stat-card">
              <h3>Total Amount</h3>
              <p>₹{getReportStats(activeTab).total.toFixed(2)}</p>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="error-message json-error">
              <pre>{error}</pre>
            </div>
          )}

          {/* Loading State */}
          {loading && (
            <div className="loading-state">
              <p>Loading report data...</p>
            </div>
          )}

          {/* Standard Report Table Content */}
          <div className="report-content">
            {renderTable(activeTab)}
          </div>

          {/* Download Button for standard tabs */}
          <div className="report-actions">
            <button 
              className="btn btn-primary"
              onClick={() => handleDownload(activeTab)}
              disabled={loading || !reports[activeTab] || reports[activeTab].length === 0}
            >
              <FiDownload /> Download {activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} Report
            </button>
          </div>
        </>
      )}
    </main>
  );
};

export default ReportsPage;